import React from "react";
import { motion } from "framer-motion";
import { TrendingUp, Clock, Percent } from "lucide-react";

interface CompactResultsSummaryProps {
  annualImpact: number;
  paybackMonths: number;
  yearOneROI: number;
  formatCurrency: (value: number) => string;
  language: string;
}

const CompactResultsSummary = ({
  annualImpact,
  paybackMonths,
  yearOneROI,
  formatCurrency,
  language
}: CompactResultsSummaryProps) => {
  return (
    <motion.div 
      className="grid grid-cols-3 gap-2 p-3 bg-gradient-to-r from-brand-600 via-brand-500 to-pink-500 rounded-lg text-white shadow-lg"
      initial={{ opacity: 0, y: -10, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -10, scale: 0.98 }}
      transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
    >
      <motion.div 
        className="text-center"
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="flex items-center justify-center gap-1 mb-1">
          <TrendingUp className="h-3 w-3" />
          <span className="text-[10px] text-white/70">
            {language === 'sv' ? 'Årlig påverkan' : 'Annual Impact'}
          </span>
        </div>
        <motion.p 
          className="text-sm sm:text-base font-bold"
          key={annualImpact}
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          {formatCurrency(annualImpact)}
        </motion.p>
      </motion.div>
      
      <motion.div 
        className="text-center border-x border-white/20"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
      >
        <div className="flex items-center justify-center gap-1 mb-1">
          <Clock className="h-3 w-3" />
          <span className="text-[10px] text-white/70">
            {language === 'sv' ? 'Återbetalning' : 'Payback'}
          </span>
        </div>
        <p className="text-sm sm:text-base font-bold">
          {paybackMonths < 1 ? '<1' : paybackMonths.toFixed(1)} {language === 'sv' ? 'mån' : 'mo'}
        </p>
      </motion.div>
      
      <motion.div 
        className="text-center"
        initial={{ opacity: 0, x: 10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="flex items-center justify-center gap-1 mb-1">
          <Percent className="h-3 w-3" />
          <span className="text-[10px] text-white/70">
            {language === 'sv' ? 'År 1 ROI' : 'Year 1 ROI'}
          </span>
        </div>
        <motion.p 
          className="text-sm sm:text-base font-bold"
          key={yearOneROI}
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          {yearOneROI.toFixed(0)}%
        </motion.p>
      </motion.div>
    </motion.div>
  );
};

export default CompactResultsSummary;
